/**
 * @ngdoc service
 * @name momentService
 * @module flowingly.services
 *
 * @description A helper service for api/flowmodels/ (previously api/workflow/) actions.
 * Note that the name workflow is used incorrectly here, it refers to Flow Models.
 *
 */

import IPublishWorkflowModel from '@Shared.Angular/@types/core/api/models/publishWorkflowModel';
import INodeCustomEmailCommandData from '@Shared.Angular/@types/core/contracts/commands/modeler/nodeCustomEmailCommandData';
import { Guid } from '@Shared.Angular/@types/guid';
import { Services } from '@Shared.Angular/@types/services';
import angular, { IHttpService, IPromise } from 'angular';
import {
  FlowinglyPermissions,
  FlowModelPublishType
} from './flowingly.constants';
import {
  IFlowModelSummary,
  IFlowModelTagSummary
} from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/flowModelSummary';
import IReportFlowModelDetail from '@Shared.Angular/@types/core/contracts/queryModel/reports/reportFlowModelDetail';
import IPublishApprovalWorkflowModel from '@Shared.Angular/@types/core/api/models/publishApprovalWorkFlowModel';
import IFlowModelTag from '@Shared.Angular/@types/core/contracts/queryModel/tagGroup/flowModelTag';
import IPublishedFlowModel from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/publishedFlowModel';
import ICategoryDetail from '@Shared.Angular/@types/core/contracts/queryModel/category/categoryDetail';
import IFlowinglySearchResult from '@Shared.Angular/@types/core/contracts/queryModel/search/flowinglySearchResult';
import IFlowModelPublishResult from '@Shared.Angular/@types/core/contracts/queryModel/workflow/flowModelPublishResult';
import {
  IFlowModelDeletedMessage,
  IFlowModelPublishedMessage,
  IFlowModelUnpublishedChangesMessage,
  IFlowModelUnpublishedMessage,
  PubSubEvent
} from '@Shared.Angular/@types/pubSub';
import IPublishedFlowModelDetail from '@Shared.Angular/@types/core/contracts/queryModel/flowModels/publishedFlowModelDetail';
import IWorkflowDetail from '@Shared.Angular/@types/core/contracts/queryModel/workflow/workflowDetail';
import { ApiDateTime } from '@Shared.Angular/@types/dateTime';

export class FlowModelApiService {
  public flowModels: IFlowModelSummary[] = [];
  public flowModelsLoaded = false;

  public deferedFlowModels: angular.IDeferred<IFlowModelSummary[]>;
  public flowModelTags: IFlowModelTagSummary[] = [];
  public deferedFlowModelTags: angular.IDeferred<IFlowModelTagSummary[]>;

  constructor(
    private $q: angular.IQService,
    private $http: IHttpService,
    private APP_CONFIG: Services.APP_CONFIG,
    private flowinglyConstants: Services.FlowinglyConstants,
    private pubsubService: Services.PubSubService,
    private sessionService: Services.SessionService
  ) {
    this.deferedFlowModels = this.$q.defer<IFlowModelSummary[]>();
    this.deferedFlowModelTags = this.$q.defer<IFlowModelTagSummary[]>();
    if (!this.APP_CONFIG.enableTagging) {
      this.deferedFlowModelTags.resolve([]);
    }

    const subscriberId = 'flowModelApiService';
    this.pubsubService.subscribeMany(
      ['SIGNALR_NEW_FLOW_MODEL_SAVED'],
      () => {
        this.getFlowModels();
        if (this.APP_CONFIG.enableTagging) {
          this.getUsedTagsInFlowModels();
        }
      },
      subscriberId
    );
    this.pubsubService.subscribe(
      'SIGNALR_WORKFLOW_UNPUBLISHED',
      (event: PubSubEvent, message: IFlowModelUnpublishedMessage) => {
        this.deferedFlowModels.promise.then((flowModels) => {
          message.summary.editableByCurrentUser =
            this.canUserEditFlowModel(message);
          const existingIndex = flowModels.findIndex(
            (model) => model.id === message.flowModelId
          );
          if (existingIndex > -1) {
            flowModels[existingIndex] = message.summary;
          } else {
            flowModels.push(message.summary);
          }
          this.deferedFlowModels = this.$q.defer<IFlowModelSummary[]>();
          this.deferedFlowModels.resolve(flowModels);
        });
      },
      subscriberId
    );
    this.pubsubService.subscribe(
      'SIGNALR_WORKFLOW_PUBLISHED',
      (event: PubSubEvent, message: IFlowModelPublishedMessage) => {
        this.deferedFlowModels.promise.then((flowModels) => {
          message.summary.editableByCurrentUser =
            this.canUserEditFlowModel(message);
          const existingIndex = flowModels.findIndex(
            (model) => model.id === message.flowModelId
          );
          if (existingIndex > -1) {
            flowModels[existingIndex] = message.summary;
          } else {
            flowModels.push(message.summary);
          }
          this.deferedFlowModels = this.$q.defer<IFlowModelSummary[]>();
          this.deferedFlowModels.resolve(flowModels);
          if (this.APP_CONFIG.enableTagging) {
            this.deferedFlowModelTags.promise.then((tags) => {
              const existingTagIndex = tags.findIndex(
                (tag) => tag.flowModelId === message.flowModelId
              );
              if (existingTagIndex > -1) {
                if (message.tagSummary) {
                  tags[existingTagIndex] = message.tagSummary;
                } else {
                  tags.splice(existingTagIndex, 1);
                }
              } else if (message.tagSummary) {
                tags.push(message.tagSummary);
              }
              this.deferedFlowModelTags =
                this.$q.defer<IFlowModelTagSummary[]>();
              this.deferedFlowModelTags.resolve(tags);
            });
          }
        });
      },
      subscriberId
    );
    this.pubsubService.subscribe(
      'SIGNALR_WORKFLOW_UNPUBLISHED_CHANGES',
      (event: PubSubEvent, message: IFlowModelUnpublishedChangesMessage) => {
        this.deferedFlowModels.promise.then((flowModels) => {
          const updatedFlowModel = flowModels.find(
            (model) => model.id === message.flowModelId
          );
          if (updatedFlowModel) {
            updatedFlowModel.hasUnPublishedChanges =
              message.hasUnpublishedChanges;
            updatedFlowModel.lastEditedDate = new Date()
              .toISOString()
              .replace('Z', '') as ApiDateTime;
          }
          this.deferedFlowModels = this.$q.defer<IFlowModelSummary[]>();
          this.deferedFlowModels.resolve(flowModels);
        });
      },
      subscriberId
    );
    this.pubsubService.subscribe(
      'SIGNALR_WORKFLOW_NAME_CHANGED',
      (event: PubSubEvent, message: string) => {
        const parsedMessage = JSON.parse(message);
        this.deferedFlowModels.promise.then((flowModels) => {
          const updatedFlowModel = flowModels.find(
            (model) => model.id === parsedMessage.id
          );
          if (updatedFlowModel) {
            updatedFlowModel.name = parsedMessage.name;
          }
          this.deferedFlowModels = this.$q.defer<IFlowModelSummary[]>();
          this.deferedFlowModels.resolve(flowModels);
        });
      },
      subscriberId
    );
    this.pubsubService.subscribe(
      'SIGNALR_SETUP_FLOW_MODEL_DELETED',
      (event: PubSubEvent, message: IFlowModelDeletedMessage) => {
        this.deferedFlowModels.promise.then((flowModels) => {
          const updatedFlowModels = flowModels.filter(
            (model) => model.id !== message.flowModelId
          );
          this.deferedFlowModels = this.$q.defer<IFlowModelSummary[]>();
          this.deferedFlowModels.resolve(updatedFlowModels);
        });
      },
      subscriberId
    );
  }

  getFlowModels(noSpinner = false) {
    const existingDeferred = this.deferedFlowModels;
    this.deferedFlowModels = this.$q.defer<IFlowModelSummary[]>();
    return this.$http
      .get<IFlowModelSummary[]>(`${this.APP_CONFIG.apiBaseUrl}flowmodels`, {
        noSpinner: noSpinner || false
      })
      .then((response) => {
        this.flowModels = response.data;
        this.flowModelsLoaded = true;
        existingDeferred.resolve(this.flowModels);
        this.deferedFlowModels.resolve(this.flowModels);
        return this.flowModels;
      });
  }

  getUsedTagsInFlowModels(noSpinner = false) {
    const existingDeferred = this.deferedFlowModelTags;
    this.deferedFlowModelTags = this.$q.defer<IFlowModelTagSummary[]>();
    return this.$http
      .get<IFlowModelTagSummary[]>(
        `${this.APP_CONFIG.apiBaseUrl}flowmodels/tags`,
        {
          noSpinner: noSpinner || false
        }
      )
      .then((response) => {
        this.flowModelTags = response.data;
        existingDeferred.resolve(this.flowModelTags);
        this.deferedFlowModelTags.resolve(this.flowModelTags);
        return response.data;
      });
  }

  getFlowModelById(flowModelId: Guid, allowDeleted = false) {
    return this.$http
      .get<IWorkflowDetail>(
        `${this.APP_CONFIG.apiBaseUrl}flowmodels/${flowModelId}?allowDeleted=${allowDeleted}`
      )
      .then((response) => response.data);
  }

  getCategoriesUserCanPublishTo(
    flowModelId: Guid,
    isPublishRequestSchema: boolean,
    noSpinner = false
  ) {
    return this.$http
      .get<ICategoryDetail[]>(
        `${
          this.APP_CONFIG.apiBaseUrl
        }flowmodels/${flowModelId}/categories?isPublishRequestSchema=${!!isPublishRequestSchema}`,
        {
          noSpinner: noSpinner || false
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  getPublishedEntities(
    flowModelId: Guid,
    isPublishApprovalRequest: boolean,
    noSpinner = false
  ) {
    return this.$http.get<IFlowinglySearchResult[]>(
      `${this.APP_CONFIG.apiBaseUrl}flowmodels/${flowModelId}/` +
        `publishedEntities?isPublishApprovalRequest=${isPublishApprovalRequest}`,
      { noSpinner: noSpinner }
    );
  }

  sendForApproval(model: IPublishApprovalWorkflowModel) {
    return this.$http.post<void>(
      `${this.APP_CONFIG.apiBaseUrl}flowmodels/${model.flowModelId}/publishApprovals`,
      model
    );
  }

  publishFlowModel(model: IPublishWorkflowModel) {
    return this.$http.post<IFlowModelPublishResult>(
      `${this.APP_CONFIG.apiBaseUrl}flowmodels/${model.flowId}/publish`,
      model
    );
  }

  unPublishFlowModel(flowModelId: Guid) {
    return this.$http.post<void>(
      `${this.APP_CONFIG.apiBaseUrl}flowmodels/${flowModelId}/unpublish`,
      {}
    );
  }

  getPublishedFlowModel(flowModelId: Guid, noSpinner = false) {
    return this.$http
      .get<IPublishedFlowModelDetail>(
        `${this.APP_CONFIG.apiBaseUrl}flowmodels/published/${flowModelId}?` +
          `maxUsersToShow=${this.flowinglyConstants.maxNumberOfUsersToShow}`,
        { noSpinner: noSpinner }
      )
      .then((response) => {
        return response.data;
      });
  }

  getPublishedFlowModels(
    noSpinner: boolean,
    publishType: FlowModelPublishType
  ) {
    return this.$http<IPublishedFlowModel[]>({
      method: 'GET',
      url: this.APP_CONFIG.apiBaseUrl + 'flowmodels/published',
      params: {
        publishType: publishType
      },
      noSpinner: noSpinner
    }).then((response) => response.data);
  }

  getReportFlowModels(noSpinner = false) {
    return this.$http
      .get<IReportFlowModelDetail[]>(
        this.APP_CONFIG.apiBaseUrl + 'reports/getFlowModelsCanReportOn',
        {
          noSpinner: noSpinner
        }
      )
      .then((response) => {
        return response.data;
      });
  }

  deleteFlowModel(flowModelId: Guid) {
    return this.$http.post<void>(
      `${this.APP_CONFIG.apiBaseUrl}flowmodels/delete/${flowModelId}`,
      {}
    );
  }

  linkFlowModelToSourceFile(flowModelId: Guid, fileId: Guid) {
    return this.$http.put<void>(
      `${this.APP_CONFIG.apiBaseUrl}flowmodels/${flowModelId}/source`,
      JSON.stringify(fileId)
    );
  }

  linkImportedFlowModelComponents(publishType: FlowModelPublishType) {
    return this.$http.get<void>(
      `${this.APP_CONFIG.apiBaseUrl}flowmodels/link/${publishType}`
    );
  }

  sendTestEmail(nodeCustomEmail: INodeCustomEmailCommandData) {
    return this.$http.post<void>(
      `${this.APP_CONFIG.apiBaseUrl}flowmodels/sendTestEmail/`,
      nodeCustomEmail
    );
  }

  async getTags(flowModelId: Guid) {
    const response = await this.$http.get<IFlowModelTag>(
      `${this.APP_CONFIG.apiBaseUrl}flowmodels/${flowModelId}/tags`
    );
    return response.data;
  }

  async getFlowModelOwners(flowModelId: Guid) {
    const response = await this.$http.get<IFlowinglySearchResult[]>(
      this.APP_CONFIG.apiBaseUrl +
        'workflow/getFlowModelOwners?flowModelId=' +
        flowModelId
    );
    return response.data;
  }

  public startTestFlow(
    flowModelId: Guid,
    flowModelSchemaId: Guid = null
  ): IPromise<Guid> {
    return this.$http
      .post<Guid>(this.APP_CONFIG.apiBaseUrl + 'flows/tests', {
        FlowModelId: flowModelId,
        FlowModelSchemaId: flowModelSchemaId
      })
      .then((response) => response.data);
  }

  private canUserEditFlowModel(
    message: IFlowModelPublishedMessage | IFlowModelUnpublishedMessage
  ): boolean {
    const user = this.sessionService.getUser();
    return (
      user.roles.some((role) =>
        role.permissions.some(
          (permission) =>
            permission.secureCode === FlowinglyPermissions.FLOWMODEL_UPDATE
        )
      ) ||
      (message.summary.creatorId === user.id &&
        user.roles.some((role) =>
          role.permissions.some(
            (permission) =>
              permission.secureCode ===
              FlowinglyPermissions.FLOWMODEL_UPDATE_OWN
          )
        )) ||
      message.ownerUserIds.includes(user.id) ||
      message.categoryEditorUserIds.includes(user.id)
    );
  }
}

angular
  .module('flowingly.services')
  .factory('flowModelApiService', [
    '$q',
    '$http',
    'APP_CONFIG',
    'flowinglyConstants',
    'pubsubService',
    'sessionService',
    (
      $q: angular.IQService,
      $http: IHttpService,
      appConfig: Services.APP_CONFIG,
      flowinglyConstants: Services.FlowinglyConstants,
      pubsubService: Services.PubSubService,
      sessionService: Services.SessionService
    ) =>
      new FlowModelApiService(
        $q,
        $http,
        appConfig,
        flowinglyConstants,
        pubsubService,
        sessionService
      )
  ]);

export type FlowModelApiServiceType = InstanceType<typeof FlowModelApiService>;
